import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import StyledCircuitModal from './StyledCircuitModal';
import { useForm, useWatch } from 'react-hook-form';
import StyledInput from '@/components/styled/StyledInput';
import SearchIcon from '@/assets/icons/search.svg?react';
import StyledRadioGroup from '@/components/styled/StyledRadioGroup';
import { Label } from '@/components/ui/label';
import StyledCheckboxGroup from '@/components/styled/StyledCheckboxGroup';
import { useCallback, useMemo } from 'react';
import { PublishedBenefitsParams } from '@/api/benefit.api';
import { useGetPublishedBenefits } from '@/hooks/useBenefits';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import HorizontalLine from '@/components/ui/HorizontalLine';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { getBenefitNameFromType } from '@/utils/benefits/getBenefitTypeName';
import CircuitModalBenefitSelectionTablesByCity, {
  BenefitFiltersType,
} from './CircuitModalBenefitSelectionTable';
import DisplaySelectionTable from './DisplaySelectionTable';
import StyledSelect from '@/components/styled/StyledSelect';
import { CIRCUIT_DAY_PERIODS } from '@/constants/circuitDayPeriods';
import { CircuitBenefitAdditionModalProps } from './GenericCircuitBenefitAdditionModal';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

const AddTransportBenefitModal = ({
  index,
  stepIndex,
  circuitControl,
  addBenefits,
  standardBenefit,
  optionalBenefits,
  closeModal,
  isViewMode = false,
}: CircuitBenefitAdditionModalProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { control } = useForm<BenefitFiltersType>({
    mode: 'onChange',
    defaultValues: {
      search: '',
      transportType: standardBenefit
        ? standardBenefit.type ===
            getBenefitNameFromType(BenefitType.VEHICLE) ||
          standardBenefit.type ===
            getBenefitNameFromType(BenefitType.VEHICLE_WITH_CHAUFFEUR)
          ? 'car'
          : 'transfer'
        : optionalBenefits && optionalBenefits.length > 0
          ? optionalBenefits[0].type ===
              getBenefitNameFromType(BenefitType.VEHICLE) ||
            optionalBenefits[0].type ===
              getBenefitNameFromType(BenefitType.VEHICLE_WITH_CHAUFFEUR)
            ? 'car'
            : 'transfer'
          : undefined,
      filters: [
        ...new Set(
          optionalBenefits.map((benefit) =>
            benefit.type !== 'ACTIVITY' ? benefit.type : 'TRANSFER',
          ),
        ),
      ].concat(
        standardBenefit
          ? [
              standardBenefit.type !== 'ACTIVITY'
                ? standardBenefit.type
                : 'TRANSFER',
            ]
          : [],
      ) as BenefitFiltersType['filters'],
      standardBenefit: standardBenefit,
      optionalBenefits: optionalBenefits,
    },
  });

  const selectedTransportType = useWatch({ control, name: 'transportType' });
  const selectedFilters = useWatch({ control, name: 'filters' });
  const selectedStandardBenefit = useWatch({
    control,
    name: 'standardBenefit',
  });
  const selectedOptionalBenefits = useWatch({
    control,
    name: 'optionalBenefits',
  });
  const nameSearchValue = useWatch({ control, name: 'search' });
  const debouncedNameSearch = useDebouncedSearch(nameSearchValue);
  const nbDays = useWatch({
    control: circuitControl,
    name: `circuitDays.${index}.steps.${stepIndex}.transport.nbDays`,
  });
  const selectedCountries = useWatch({
    control: circuitControl,
    name: `countries`,
  });

  const countries = useMemo(
    () => selectedCountries.map(({ country }) => country.value),
    [selectedCountries],
  );

  const filterOptions = useMemo(
    () =>
      selectedTransportType
        ? selectedTransportType === 'car'
          ? [
              {
                label: t('circuits.editorial.transportFilters.carRental'),
                value: 'VEHICLE',
              },
              {
                label: t(
                  'circuits.editorial.transportFilters.vehicleWithChauffeur',
                ),
                value: 'VEHICLE_WITH_CHAUFFEUR',
              },
            ]
          : [
              {
                label: t('circuits.editorial.transportFilters.transfers'),
                value: 'TRANSFER',
              },
              {
                label: t('circuits.editorial.transportFilters.flights'),
                value: 'VOL',
              },
            ]
        : [],
    [selectedTransportType, t],
  );

  const filterBenefitsOnTransportType = useCallback(
    (benefits: BenefitFormData[]) =>
      benefits.filter((benefit) =>
        selectedTransportType === 'car'
          ? benefit.type === getBenefitNameFromType(BenefitType.VEHICLE) ||
            benefit.type ===
              getBenefitNameFromType(BenefitType.VEHICLE_WITH_CHAUFFEUR)
          : benefit.type !== getBenefitNameFromType(BenefitType.VEHICLE) &&
            benefit.type !==
              getBenefitNameFromType(BenefitType.VEHICLE_WITH_CHAUFFEUR),
      ),
    [selectedTransportType],
  );

  const benefitsFilters: PublishedBenefitsParams = useMemo(() => {
    if (selectedFilters && selectedFilters.length) {
      const allFilters = selectedFilters
        .filter((benefitType) =>
          selectedTransportType === 'car'
            ? benefitType === 'VEHICLE' ||
              benefitType === 'VEHICLE_WITH_CHAUFFEUR'
            : benefitType !== 'VEHICLE' &&
              benefitType !== 'VEHICLE_WITH_CHAUFFEUR',
        )
        .map((benefitType) =>
          benefitType === 'TRANSFER' ? 'ACTIVITY' : benefitType,
        );
      return allFilters.includes('ACTIVITY')
        ? {
            types: allFilters,
            activityType: 'TRANSFER',
            name: debouncedNameSearch,
            countries,
          }
        : { types: allFilters, name: debouncedNameSearch, countries };
    }
    return { types: [], name: debouncedNameSearch, countries };
  }, [countries, debouncedNameSearch, selectedFilters, selectedTransportType]);

  const { data: publishedBenefits } = useGetPublishedBenefits(benefitsFilters);

  const isModificationMode = useMemo(() => {
    return Boolean(standardBenefit || optionalBenefits.length > 0);
  }, [standardBenefit, optionalBenefits]);

  return (
    <StyledCircuitModal
      title={t('circuits.editorial.modalTitles.addTransport')}
      closeModal={closeModal}
      actionOnClick={() => {
        addBenefits({
          standardBenefit: selectedStandardBenefit,
          optionalBenefits: filterBenefitsOnTransportType(
            selectedOptionalBenefits ?? [],
          ),
          stepNbDays:
            selectedTransportType === 'car' ? (nbDays ?? 1) : undefined,
        });
      }}
      actionTitle={
        isModificationMode
          ? t('circuits.editorial.modalActions.save')
          : t('circuits.editorial.modalActions.add')
      }
      isActionDisabled={isViewMode}
    >
      <StyledRadioGroup
        control={control}
        name="transportType"
        options={[
          {
            label: t('circuits.editorial.transportTypes.transfersFlights'),
            value: 'transfer',
          },
          {
            label: t('circuits.editorial.transportTypes.carRentalChauffeur'),
            value: 'car',
          },
        ]}
        displayInColumns
        disabled={isViewMode}
      />
      {selectedTransportType === 'car' && (
        <div className="flex gap-4 items-center">
          <Label className="text-gray-700">
            {t('circuits.editorial.numberOfDays')}
          </Label>
          <div>
            <StyledInput
              control={circuitControl}
              name={`circuitDays.${index}.steps.${stepIndex}.transport.nbDays`}
              type="number"
              placeholder={t('circuits.editorial.numberOfDaysPlaceholder')}
              min={0}
              disabled={isViewMode}
            />
          </div>
        </div>
      )}
      <StyledInput
        control={control}
        name="search"
        placeholder={t('circuits.editorial.searchPlaceholder')}
        title={t('circuits.editorial.searchTitle')}
        icon={<SearchIcon className="size-5 stroke-gray-500" />}
        disabled={isViewMode}
      />
      {selectedTransportType && (
        <Label className="text-gray-700">
          {t('circuits.editorial.filters')}
        </Label>
      )}
      <StyledCheckboxGroup
        control={control}
        name="filters"
        options={filterOptions}
        displayInColumns
        displayAllOption={false}
        isViewMode={isViewMode}
      />
      <DisplaySelectionTable
        index={index}
        stepIndex={stepIndex}
        type="Transport"
        control={circuitControl}
        isViewMode={isViewMode}
      />
      <StyledSelect
        control={circuitControl}
        name={`circuitDays.${index}.steps.${stepIndex}.transport.time`}
        options={getTranslatedConst(CIRCUIT_DAY_PERIODS)}
        title={t('circuits.editorial.period')}
        placeholder={t('circuits.editorial.periodPlaceholder')}
        isClearable
        disabled={isViewMode}
      />
      {selectedTransportType === 'car' &&
        selectedFilters.includes('VEHICLE') && (
          <>
            <HorizontalLine />
            <CircuitModalBenefitSelectionTablesByCity
              control={control}
              publishedBenefits={publishedBenefits ?? {}}
              benefitType={BenefitType.VEHICLE}
              title={t('circuits.editorial.transportTitles.carRental')}
              isViewMode={isViewMode}
            />
          </>
        )}
      {selectedTransportType === 'car' &&
        selectedFilters.includes('VEHICLE_WITH_CHAUFFEUR') && (
          <>
            <HorizontalLine />
            <CircuitModalBenefitSelectionTablesByCity
              control={control}
              publishedBenefits={publishedBenefits ?? {}}
              benefitType={BenefitType.VEHICLE_WITH_CHAUFFEUR}
              title={t(
                'circuits.editorial.transportTitles.vehicleWithChauffeur',
              )}
              isViewMode={isViewMode}
            />
          </>
        )}
      {selectedTransportType === 'transfer' &&
        selectedFilters.includes('VOL') && (
          <>
            <HorizontalLine />
            <CircuitModalBenefitSelectionTablesByCity
              control={control}
              publishedBenefits={publishedBenefits ?? {}}
              benefitType={BenefitType.VOL}
              title={t('circuits.editorial.transportTitles.flights')}
              isViewMode={isViewMode}
            />
          </>
        )}
      {selectedTransportType === 'transfer' &&
        selectedFilters.includes('TRANSFER') && (
          <>
            <HorizontalLine />
            <CircuitModalBenefitSelectionTablesByCity
              control={control}
              publishedBenefits={publishedBenefits ?? {}}
              benefitType={BenefitType.ACTIVITY}
              title={t('circuits.editorial.transportTitles.transfers')}
              isViewMode={isViewMode}
            />
          </>
        )}
    </StyledCircuitModal>
  );
};

export default AddTransportBenefitModal;
